import {invalidateQueries, q} from "../../state";
import {
  ApiDateTime,
  UnderstandingMin,
  CreateUnderstanding,
  CreateTprmScope,
  CreateEngagementRequest,
  Engagement,
  EngagementId,
  EngagementLock,
  EngagementTask,
  ScopeBadge,
  TprmScopeMin,
  TprmExternalQuestionnaireMin,
  DiagramMin,
  CreateDiagram,
  OwnerId,
  EngagementStatus,
  PaginationArgs,
  FilterArgs,
} from "platformed-client/types";
import {platformedClient} from "..";

export async function search(query: PaginationArgs, body: FilterArgs) {
  return await platformedClient.internalRequest("post", "/region/{region}/api/tprm/engagements/search", {query, body});
}

export async function create(engagement: CreateEngagementRequest): Promise<Engagement> {
  const res = await platformedClient.internalRequest("post", "/region/{region}/api/tprm/engagements", {
    body: engagement,
  });
  await invalidateQueries([
    q.tprm.thirdParty.filter(engagement.third_party_id),
    // Data rooms
    q.tprm.thirdPartyEngagements.filter(),
    q.tprm.engagements.filter(),
  ]);
  return res;
}

export async function get(engagementId: EngagementId): Promise<Engagement> {
  return platformedClient.internalRequest("get", "/region/{region}/api/tprm/engagements/{engagement_id}", {
    path: {engagement_id: engagementId},
  });
}

export async function listScopes(engagementId: EngagementId): Promise<TprmScopeMin[]> {
  return platformedClient.internalRequest("get", "/region/{region}/api/tprm/engagements/{engagement_id}/scopes", {
    path: {engagement_id: engagementId},
  });
}

/**
 * Fetch source error counts per scope for a data room.
 *
 * Only scopes with at least one errored source are returned — treat a missing
 * entry as a zero count.  This is the data source for badge/indicator UIs and
 * exists separately from `listScopes` to avoid embedding counts in every
 * `TprmScopeMin` fetch.
 */
export async function listScopeBadges(engagementId: EngagementId): Promise<ScopeBadge[]> {
  return platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/engagements/{engagement_id}/scopes/badges",
    {path: {engagement_id: engagementId}},
  );
}

export async function createScope(engagementId: EngagementId, scope: CreateTprmScope) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/scopes",
    {path: {engagement_id: engagementId}, body: scope},
  );
  await invalidateQueries([q.tprm.engagementScopes.filter(engagementId), q.tprm.complexityScoping.filter()]);
  return res;
}

export async function listDiagrams(engagementId: EngagementId): Promise<DiagramMin[]> {
  return platformedClient.internalRequest("get", "/region/{region}/api/tprm/engagements/{engagement_id}/diagrams", {
    path: {engagement_id: engagementId},
  });
}

export async function createDiagram(engagementId: EngagementId, diagram: CreateDiagram): Promise<DiagramMin> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/diagrams",
    {path: {engagement_id: engagementId}, body: diagram},
  );
  await invalidateQueries([q.tprm.engagementDiagrams.filter(engagementId)]);
  return res;
}

export async function listUnderstandings(engagementId: EngagementId): Promise<UnderstandingMin[]> {
  return platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/engagements/{engagement_id}/understandings",
    {path: {engagement_id: engagementId}},
  );
}

export async function createUnderstanding(
  engagementId: EngagementId,
  understanding: CreateUnderstanding,
): Promise<UnderstandingMin> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/understandings",
    {path: {engagement_id: engagementId}, body: understanding},
  );
  await invalidateQueries([q.tprm.engagementUnderstandings.filter(engagementId)]);
  return res;
}

export async function listExternalQuestionnaires(engagementId: EngagementId): Promise<TprmExternalQuestionnaireMin[]> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/engagements/{engagement_id}/external_questionnaires",
    {path: {engagement_id: engagementId}},
  );
}

export async function updateName(engagementId: EngagementId, name: string): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/name",
    {path: {engagement_id: engagementId}, body: name},
  );
  await invalidateQueries([
    q.tprm.engagements.filter(),
    q.tprm.engagement.filter(engagementId),
    q.tprm.thirdPartyEngagements.filter(),
  ]);
  return res;
}

export async function assign(engagementId: EngagementId, owner: OwnerId) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/owner",
    {path: {engagement_id: engagementId}, body: owner},
  );
  await invalidateQueries([
    q.tprm.engagements.filter(),
    q.tprm.engagement.filter(engagementId),
    q.tprm.thirdPartyEngagements.filter(),
  ]);
  return res;
}

export async function updateStatus(engagementId: EngagementId, status: EngagementStatus) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/status",
    {path: {engagement_id: engagementId}, body: status},
  );
  await invalidateQueries([
    q.tprm.engagements.filter(),
    q.tprm.engagement.filter(engagementId),
    q.tprm.thirdPartyEngagements.filter(),
  ]);
  return res;
}

export async function delete_(engagementId: EngagementId): Promise<string> {
  const res = await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/engagements/{engagement_id}",
    {path: {engagement_id: engagementId}},
  );
  await invalidateQueries([q.tprm.thirdPartyEngagements.filter()]);
  return res;
}

export async function canDelete(engagementId: EngagementId): Promise<boolean> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/engagements/{engagement_id}/deletable",
    {path: {engagement_id: engagementId}},
  );
}

export async function updateEngagementWindowStart(
  engagementId: EngagementId,
  date: ApiDateTime | null,
): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/engagement_window_start",
    {path: {engagement_id: engagementId}, body: date},
  );
  await invalidateQueries([
    q.tprm.engagements.filter(),
    q.tprm.engagement.filter(engagementId),
    q.tprm.thirdPartyEngagements.filter(),
  ]);
  return res;
}

export async function updateEngagementWindowEnd(engagementId: EngagementId, date: ApiDateTime | null): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/engagement_window_end",
    {path: {engagement_id: engagementId}, body: date},
  );
  await invalidateQueries([
    q.tprm.engagements.filter(),
    q.tprm.engagement.filter(engagementId),
    q.tprm.thirdPartyEngagements.filter(),
  ]);
  return res;
}

export async function updateEngagementType(engagementId: EngagementId, engagementType: string | null): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/engagement_type",
    {path: {engagement_id: engagementId}, body: engagementType},
  );
  await invalidateQueries([
    q.tprm.engagements.filter(),
    q.tprm.engagement.filter(engagementId),
    q.tprm.thirdPartyEngagements.filter(),
  ]);
  return res;
}

export async function updateExternalId(engagementId: EngagementId, externalId: string): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/external_id",
    {path: {engagement_id: engagementId}, body: externalId},
  );
  await invalidateQueries([
    q.tprm.engagements.filter(),
    q.tprm.engagement.filter(engagementId),
    q.tprm.thirdPartyEngagements.filter(),
  ]);
  return res;
}

export async function listEngagementTasks(lock: EngagementLock): Promise<EngagementTask[]> {
  return await platformedClient.internalRequest("post", "/region/{region}/api/tprm/engagements/tasks", {body: lock});
}
